import Localizer from 'localizer';
import Personalizer from 'personalizer';
import { isMale } from './UserUtil';

export const getPercentageOfGoal = (value, goal) => {
    return Math.floor((value / goal) * 100);
};

export const MaxHydrationValues = {
    goal: {
        cup: 80,
        oz: 640,
        ml: 19000
    },
    container: {
        cup: 16,
        oz: 128,
        ml: 3785
    },
    intake: {
        cup: 277,
        oz: 2216,
        ml: 65534
    }
};

export const getHydrationMaxGoal = (hydrationUnit) => {
    return MaxHydrationValues.goal[Object.keys(FluidTypes).find((key) => FluidTypes[key] === hydrationUnit)];
};

export const getHydrationMaxContainer = (hydrationUnit) => {
    return MaxHydrationValues.container[Object.keys(FluidTypes).find((key) => FluidTypes[key] === hydrationUnit)];
};

export const DefaultContainers = {
    cup: [1, 2, 3],
    oz: [8, 16, 24],
    ml: [250, 500, 750]
};

// Enum that represents the different types of fluid measurements
// The order of the keys in the object below matters! Do not change the order unless specifically required.
export const FluidTypes = {
    cup: 'cup',
    oz: 'ounce',
    ml: 'milliliter'
};

export const getDefaultContainers = (hydrationUnit) => {
    const unitKey = Object.keys(FluidTypes).find((key) => FluidTypes[key] === hydrationUnit);
    const containers = [];

    DefaultContainers[unitKey].forEach((volume, index) => {
        containers.push({
            name: Localizer.localize('container_number', index + 1),
            volume: volume,
            unit: hydrationUnit
        });
    });

    return containers;
};

export const HydrationHelpValues = {
    dailyIntakeFemaleOz: 91,
    dailyIntakeMaleOz: 125,
    commonGoalMayoClinicOz: 64,
    commonGoalFemaleOz: 72,
    commonGoalMaleOz: 96,
    dailyIntakeFemaleL: 2.7,
    dailyIntakeMaleL: 3.7,
    commonGoalMayoClinicMl: 1900,
    commonGoalFemaleMl: 2100,
    commonGoalMaleMl: 2800,
    commonGoalC: 8
};

export const getHydrationHelpData = (hydrationUnit, userSettings) => {
    let hydrationHelpData = {};

    switch (hydrationUnit) {
        case FluidTypes.cup:
            hydrationHelpData.hydrationDailyIntakeFemale = HydrationHelpValues.dailyIntakeFemaleOz;
            hydrationHelpData.hydrationDailyIntakeMale = HydrationHelpValues.dailyIntakeMaleOz;
            hydrationHelpData.hydrationCommonGoal = HydrationHelpValues.commonGoalMayoClinicOz;
            hydrationHelpData.hydrationDefaultGoal = isMale(userSettings)
                ? HydrationHelpValues.commonGoalMaleOz
                : HydrationHelpValues.commonGoalFemaleOz;
            hydrationHelpData.fluidType1 = Localizer.localize('cup_unit_pl');
            hydrationHelpData.fluidType2 = Localizer.localize('oz_unit').toLowerCase();
            break;

        case FluidTypes.oz:
            hydrationHelpData.hydrationDailyIntakeFemale = HydrationHelpValues.dailyIntakeFemaleOz;
            hydrationHelpData.hydrationDailyIntakeMale = HydrationHelpValues.dailyIntakeMaleOz;
            hydrationHelpData.hydrationCommonGoal = HydrationHelpValues.commonGoalMayoClinicOz;
            hydrationHelpData.hydrationDefaultGoal = isMale(userSettings)
                ? HydrationHelpValues.commonGoalMaleOz
                : HydrationHelpValues.commonGoalFemaleOz;
            hydrationHelpData.fluidType1 = Localizer.localize('oz_unit').toLowerCase();
            hydrationHelpData.fluidType2 = Localizer.localize('oz_unit').toLowerCase();
            break;
        case FluidTypes.ml:
            hydrationHelpData.hydrationDailyIntakeFemale = Personalizer.formatDecimalNumber(
                HydrationHelpValues.dailyIntakeFemaleL,
                1
            );
            hydrationHelpData.hydrationDailyIntakeMale = Personalizer.formatDecimalNumber(
                HydrationHelpValues.dailyIntakeMaleL,
                1
            );
            hydrationHelpData.hydrationCommonGoal = Personalizer.formatDecimalNumber(
                HydrationHelpValues.commonGoalMayoClinicMl / 1000,
                1
            );
            hydrationHelpData.hydrationDefaultGoal = isMale(userSettings)
                ? HydrationHelpValues.commonGoalMaleMl
                : HydrationHelpValues.commonGoalFemaleMl;
            hydrationHelpData.fluidType1 = Localizer.localize('ml_unit').toLowerCase();
            hydrationHelpData.fluidTypeAlternate = Localizer.localize('l_unit').toLowerCase();
            break;
    }
    return hydrationHelpData;
};

export const getVolumeInML = (volume, unit) => {
    let val = 0;

    switch (unit) {
        case FluidTypes.ml:
            val = volume;
            break;
        case FluidTypes.oz:
            val = volume * Personalizer.ConversionFactors.FluidOuncesToMilliliters;
            break;
        case FluidTypes.cup:
            val = volume * Personalizer.ConversionFactors.CupsToMilliliters;
            break;
    }
    // round to 4th decimal place
    val = Math.round(val * 10000) / 10000;

    return val;
};

export const getHydrationGoalValue = (goal, userSettings) => {
    const hydrationUnit = getHydrationUnit(userSettings);

    return Personalizer.convertFluid(goal, hydrationUnit, true);
};

export const getHydrationValue = (value, userSettings) => {
    const hydrationUnit = getHydrationUnit(userSettings);

    return Personalizer.personalizeFluid(value || 0, hydrationUnit, false);
};

export const getHydrationUnit = (userSettings) => {
    return userSettings &&
        userSettings.userData &&
        typeof userSettings.userData.hydrationMeasurementUnit !== 'undefined'
        ? userSettings.userData.hydrationMeasurementUnit
        : getDefaultHydrationUnit(userSettings);
};

export const getDefaultHydrationUnit = (userSettings) => {
    return userSettings?.userData.measurementSystem === Personalizer.MeasurementSystems.METRIC
        ? FluidTypes.ml
        : FluidTypes.cup;
};

export const getHydrationContainers = (userSettings) => {
    return userSettings.userData && typeof userSettings.userData.hydrationContainers !== 'undefined'
        ? userSettings.userData.hydrationContainers
        : [];
};
